import ReactDOM from 'react-dom/client'
import { StrictMode, Suspense, useEffect, lazy } from 'react'
import './index.css'
import PatientNavbar from './components/ui/layouts/patientNavbar.tsx'
import DoctorNavbar from './components/ui/layouts/managerNavbar.tsx'
import {
    Outlet,
    RouterProvider,
    ScrollRestoration,
    createBrowserRouter,
} from 'react-router-dom'
import Loading from './components/loading/index.tsx'
import {
    ClerkProvider,
    Protect,
    RedirectToSignIn,
    SignedOut,
} from '@clerk/clerk-react'
import React from 'react'
import { zhTW } from '@clerk/localizations'
import { TokenProvider } from './TokenProvider.tsx'
import { AlertProvider } from './AlertProvider.tsx'
import { CommonProvider } from './CommonProvider.tsx'
import ReactGA from 'react-ga4'
import RootLayout from '@/components/layout/rootLayout'
import { RelayEnvironmentProvider } from 'react-relay'
import { RelayEnvironment } from './RelayEnvironment'

// Lazy load all page components
// const Index = lazy(() => import('./components/pages/index/index'))
import Index from './components/pages/app/index'
import VideoList from './components/pages/app/videoList.tsx'
import InsuranceNavbar from './components/ui/layouts/insuranceNavbar.tsx'
import RequestCare from './components/pages/insurance/requestCare.tsx'
import RequestCareDetail from './components/pages/insurance/requestCareDetail.tsx'
import CareTeamNavbar from './components/ui/layouts/careTeamNavbar.tsx'
import Plan from './components/pages/insurance/plan.tsx'
import AfterReciveInvite from './components/pages/app/afterReciveInvite.tsx'
import AiConsult from './components/pages/patient/aiConsult.tsx'
import AiConsultDetail from './components/pages/patient/aiConsultDetail.tsx'
import IntroduceEditor from './components/pages/insurance/introduceEditor.tsx'
import PatientPersonalInfo from './components/pages/patient/personalInfo.tsx'
const MedicalCenterList = lazy(
    () => import('./components/pages/app/medicalCenterList')
)
const MedicalCenterDetail = lazy(
    () => import('./components/pages/app/medicalCenterDetail')
)
const AnesthesiologistList = lazy(
    () => import('./components/pages/app/anesthesiologistList')
)
const AnesthesiologistDetail = lazy(
    () => import('./components/pages/app/anesthesiologistDetail')
)
const AdminPersonalInfo = lazy(() => import('./components/pages/admin/personalInfo'))
const InsurancePersonalInfo = lazy(() => import('./components/pages/insurance/personalInfo'))
const RequestChat = lazy(() => import('./components/pages/insurance/requestChat'))

const AdminAnesth = lazy(() => import('./components/pages/admin/adminAnesth'))
const Organization = lazy(() => import('./components/pages/admin/organization'))
const LoginIn = lazy(() => import('./components/pages/app/loginIn'))
const AfterLogin = lazy(() => import('./components/pages/app/afterLogin'))
const IntroduceEditorMain = lazy(
    () => import('./components/pages/admin/introduceEditorMain')
)
const AdminMedicalCenter = lazy(
    () => import('./components/pages/admin/adminMedicalCenter')
)
const AdminHospitalIntroduceEditor = lazy(
    () => import('./components/pages/admin/adminHospitalIntroduceEditor')
)
const Privacy = lazy(() => import('./components/pages/app/privacy'))


const CareForCareTeam = lazy(() => import('./components/pages/care/care'))
const CareDetailForCareTeam = lazy(() => import('./components/pages/care/careDetail'))

// Add ChatPage lazy loading
const ChatPage = lazy(() => import('./components/ChatPage'))

const router = createBrowserRouter([
    {
        children: [
            {
                element: (
                    <>
                        <ScrollRestoration
                            getKey={(location) => location.pathname}
                        />
                        <div>
                            <div className="sticky top-0 z-50">
                                <PatientNavbar />
                                <div id="filter-container"></div>
                            </div>
                            <Suspense fallback={<Loading />}>
                                <Outlet />
                            </Suspense>
                        </div>
                    </>
                ),
                path: '/',
                children: [
                    {
                        path: '/',
                        element: <Index />,
                    },
                    {
                        path: '/chat',
                        element: <ChatPage />,
                    },
                    {
                        path: '/login',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <LoginIn />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/after-login',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AfterLogin />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/after-recive-invite',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AfterReciveInvite />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/hospital',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <MedicalCenterList />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/hospital/detail',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <MedicalCenterDetail />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/anesthesiologist',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AnesthesiologistList />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/anesthesiologist/detail',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AnesthesiologistDetail />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/privacy',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <Privacy />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/VideoList',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <VideoList />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                element: (
                    <>
                        <Protect
                            condition={(has) =>
                                has({ role: 'org:anesthes_content_manager' }) ||
                                has({
                                    role: 'org:medicalcenter_content_manager',
                                }) ||
                                has({ role: 'org:admin' })
                            }
                            fallback={<h2>您無相關權限。</h2>}
                        >
                            <SignedOut>
                                <RedirectToSignIn />
                            </SignedOut>
                            <ScrollRestoration
                                getKey={(location) => location.pathname}
                            />
                            <DoctorNavbar />
                            <Suspense fallback={<Loading />}>
                                <Outlet />
                            </Suspense>
                        </Protect>
                    </>
                ),
                path: 'admin',
                children: [
                    {
                        path: '/admin/introduce-editor',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <IntroduceEditorMain />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/admin/personal-info',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AdminPersonalInfo />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/admin/organization',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <Organization />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/admin/anesth-list',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AdminAnesth />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/admin/medical-center-list',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AdminMedicalCenter />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/admin/admin-medical-center',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AdminHospitalIntroduceEditor />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                element: (
                    <>
                        <ScrollRestoration
                            getKey={(location) => location.pathname}
                        />
                        <div>
                            <div className="sticky top-0 z-50">
                                <PatientNavbar />
                                <div id="filter-container"></div>
                            </div>
                            <Suspense fallback={<Loading />}>
                                <Outlet />
                            </Suspense>
                        </div>
                    </>
                ),
                path: 'patient',
                children: [
                    {
                        path: '/patient/personal-info',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <PatientPersonalInfo />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/patient/ai-consult',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AiConsult />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/patient/ai-consult-detail',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <AiConsultDetail />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                element: (
                    <>
                        <Protect
                            condition={(has) =>
                                has({ role: 'org:insurance_agent' })
                            }
                            fallback={<h2>您無相關權限。</h2>}
                        >
                            <SignedOut>
                                <RedirectToSignIn />
                            </SignedOut>
                            <ScrollRestoration
                                getKey={(location) => location.pathname}
                            />
                            <InsuranceNavbar />
                            <Suspense fallback={<Loading />}>
                                <Outlet />
                            </Suspense>
                        </Protect>

                        <ScrollRestoration
                            getKey={(location) => location.pathname}
                        />
                    </>
                ),
                path: 'insurance',
                children: [
                    {
                        path: '/insurance/personal-info',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <InsurancePersonalInfo />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/insurance/introduce-editor',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <IntroduceEditor />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/insurance/plan',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <Plan />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/insurance/request-chat',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <RequestChat />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/insurance/request-care',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <RequestCare />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/insurance/request-care/detail',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <RequestCareDetail />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                element: (
                    <>
                        <ScrollRestoration
                            getKey={(location) => location.pathname}
                        />
                        <div>
                            <div className="sticky top-0 z-50">
                                <CareTeamNavbar />
                                <div id="filter-container"></div>
                            </div>
                            <Suspense fallback={<Loading />}>
                                <Outlet />
                            </Suspense>
                        </div>
                    </>
                ),
                path: 'care',
                children: [
                    {
                        path: '/care/care',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <CareForCareTeam />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/care/careDetail',
                        element: (
                            <Suspense fallback={<Loading />}>
                                <CareDetailForCareTeam />
                            </Suspense>
                        ),
                    },
                ],
            },
        ],
    },
])

const App = () => {
    const TRACKING_ID = 'G-C5HNLVHZPL'
    useEffect(() => {
        ReactGA.initialize(TRACKING_ID)
    }, [])

    return (
        <TokenProvider>
            <AlertProvider>
                <CommonProvider>
                    <StrictMode>
                        <RelayEnvironmentProvider
                            environment={RelayEnvironment}
                        >
                            <RootLayout>
                                <RouterProvider router={router} />
                            </RootLayout>
                        </RelayEnvironmentProvider>
                    </StrictMode>
                </CommonProvider>
            </AlertProvider>
        </TokenProvider>
    )
}

// Import your publishable key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!PUBLISHABLE_KEY) {
    throw new Error('Missing Publishable Key')
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <ClerkProvider
            publishableKey={PUBLISHABLE_KEY}
            localization={zhTW}
            afterSignOutUrl="/"
        >
            <App />
        </ClerkProvider>
    </React.StrictMode>
)
